import React, { useState } from "react";
import styled from "styled-components";
import { requestSlackInvite } from "../../lib/functions";

const MAX_MESSAGE_CHARS = 500;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
`;

const FieldOptional = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-left: 8px;
`;

const Input = styled.input`
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--nd-border-visible);
  padding: 8px 0;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-display);
  outline: none;
  transition: border-color 0.15s ease-out;

  &::placeholder {
    color: var(--nd-text-disabled);
    font-family: 'Space Grotesk', sans-serif;
  }

  &:focus {
    border-bottom-color: var(--nd-text-display);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  background: transparent;
  border: 1px solid var(--nd-border-visible);
  border-radius: 4px;
  padding: 12px;
  min-height: 100px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-display);
  outline: none;
  resize: vertical;
  transition: border-color 0.15s ease-out;

  &::placeholder {
    color: var(--nd-text-disabled);
  }

  &:focus {
    border-color: var(--nd-text-display);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CharCount = styled.div`
  align-self: flex-end;
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--nd-text-disabled);
  margin-top: 4px;
`;

const Submit = styled.button`
  align-self: flex-start;
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-black);
  background: var(--nd-text-display);
  border: 1px solid var(--nd-text-display);
  border-radius: 999px;
  padding: 12px 24px;
  cursor: pointer;
  transition: opacity 0.18s ease-out;
  &:hover { opacity: 0.85; }
  &:disabled {
    opacity: 0.4;
    cursor: wait;
  }
`;

const ErrorLine = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nd-accent, #ff5b5b);
  margin-top: -16px;
`;

const SuccessBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SuccessLabel = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--nd-text-display);
`;

const SuccessLine = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  line-height: 1.6;
  color: var(--nd-text-primary);
  margin: 0;
  max-width: 60ch;
`;

const SlackRequestForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "submitting") return;
    setState("submitting");
    setErrorMsg("");
    try {
      await requestSlackInvite({ name, email, message });
      setState("success");
    } catch (err) {
      const msg = (err && err.message) || "something went wrong";
      setErrorMsg(msg);
      setState("error");
    }
  };

  if (state === "success") {
    return (
      <SuccessBlock>
        <SuccessLabel>[REQUEST RECEIVED]</SuccessLabel>
        <SuccessLine>I read every one myself. Expect a reply within 48 hours.</SuccessLine>
      </SuccessBlock>
    );
  }

  const disabled = state === "submitting";

  return (
    <Form onSubmit={submit} noValidate={false}>
      <Field>
        <FieldLabel>Name</FieldLabel>
        <Input
          type="text"
          required
          value={name}
          onChange={(e) => { const v = e.target.value; setName(v); }}
          placeholder="Your full name"
          maxLength={100}
          disabled={disabled}
          autoComplete="name"
        />
      </Field>

      <Field>
        <FieldLabel>Email</FieldLabel>
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => { const v = e.target.value; setEmail(v); }}
          placeholder="you@company.com"
          disabled={disabled}
          autoComplete="email"
        />
      </Field>

      <Field>
        <FieldLabel>
          Why<FieldOptional>optional</FieldOptional>
        </FieldLabel>
        <TextArea
          value={message}
          onChange={(e) => { const v = e.target.value; setMessage(v); }}
          placeholder="A sentence or two about what you're working on and why Slack, not email."
          maxLength={MAX_MESSAGE_CHARS}
          disabled={disabled}
        />
        <CharCount>{message.length}/{MAX_MESSAGE_CHARS}</CharCount>
      </Field>

      {state === "error" && errorMsg && (
        <ErrorLine>[ERROR: {errorMsg}]</ErrorLine>
      )}

      <Submit type="submit" disabled={disabled}>
        {disabled ? "SENDING…" : "COUNT ME IN →"}
      </Submit>
    </Form>
  );
};

export default SlackRequestForm;
